import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { UnreadBadge } from "@/components/shared/UnreadBadge";
import { TONE_TEXT } from "@/lib/widget-status";
import type { ProjectCommentUnread } from "@/hooks/use-project-comment-reads";

interface ProjectGroupUpdatesIndicatorProps {
  /** Ulest-oppsummering for prosjektet. undefined = ikke lastet / ingen uleste. */
  unread?: ProjectCommentUnread;
  onOpen: () => void;
  /** Skjul «Du er nevnt»-teksten (trange flater, f.eks. mobilkort). */
  compact?: boolean;
  className?: string;
}

/**
 * VIB-2180: oppdateringsikon med ulest-badge på prosjektgruppe-headeren i
 * Mine oppgaver, pluss tekstvarsel når brukeren er @-nevnt i en ulest
 * oppdatering. Klikk åpner ProjectUpdatesDialog (via onOpen) uten å
 * ekspandere/kollapse gruppen (stopPropagation).
 *
 * Teksten bruker advarsels-tonen fra widget-status-paletten (oransje =
 * «trenger oppmerksomhet»), ikke egne farger.
 */
export function ProjectGroupUpdatesIndicator({
  unread,
  onOpen,
  compact = false,
  className,
}: ProjectGroupUpdatesIndicatorProps) {
  const count = unread?.unread_count ?? 0;
  const mentioned = !!unread?.has_mention && count > 0;

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {mentioned && !compact && (
        <span className={cn("text-[11px] font-medium whitespace-nowrap", TONE_TEXT.near)}>
          Du er nevnt i en oppdatering
        </span>
      )}
      <button
        type="button"
        aria-label="Prosjektoppdateringer"
        title={mentioned ? "Du er nevnt i en oppdatering" : "Prosjektoppdateringer"}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        className={cn(
          "relative inline-flex items-center justify-center w-10 h-10 -my-3 shrink-0 text-muted-foreground hover:text-foreground",
          count > 0 && "text-foreground",
          mentioned && TONE_TEXT.near,
        )}
      >
        <SrIcon name="message-square" className="w-4 h-4" />
        <UnreadBadge count={count} className="top-1 right-1" />
      </button>
    </div>
  );
}

export default ProjectGroupUpdatesIndicator;
